import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, GraduationCap, HeartHandshake, Briefcase, BookOpen, Sparkles, Clock, Camera, Upload, X as XIcon, CheckCircle2 } from "lucide-react";

const ROLES = [
  { id: "student", label: "Student", icon: GraduationCap, desc: "Apply for internships or take test series." },
  { id: "volunteer", label: "Volunteer", icon: HeartHandshake, desc: "Help manage programs & events (admin approval required)." },
];

const INTERESTS = [
  { id: "internship", label: "Internship", icon: Briefcase, desc: "Apply for internship vacancies." },
  { id: "test_series", label: "Test Series", icon: BookOpen, desc: "Take proctored online exams." },
  { id: "both", label: "Both", icon: Sparkles, desc: "Access both tracks." },
];

const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function Register() {
  const { register, user: authUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Admin-create-student mode: invoked from /admin/users → "+ Add Student"
  // The same comprehensive form is reused; submit goes to the admin endpoint
  // (which doesn't auto-login the new user) instead of /auth/register.
  const isAdminCreate = searchParams.get("admin_create") === "1" && authUser?.role === "admin";
  const adminRolePreset = searchParams.get("role"); // "student" | "volunteer"
  const [form, setForm] = useState({
    // Account
    name: "", email: "", password: "", phone: "", role: "student",
    // Interest (student)
    interest: "both",
    // Photo
    photo_url: "",
    // Personal
    date_of_birth: "", age: "", gender: "",
    address: "", state: "", city: "", occupation: "",
    // Academic
    university: "", college: "", department: "", honors: "", stream: "",
    university_roll_number: "", registration_number: "",
    // Family
    father_name: "", father_occupation: "",
    mother_name: "", mother_occupation: "",
    // Emergency
    emergency_contact_name: "", emergency_contact_relation: "", emergency_contact_phone: "",
    // Referral
    referral_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [otpStage, setOtpStage] = useState("none");  // none | sending | sent | verifying | verified
  const [otpCode, setOtpCode] = useState("");
  const [otpResendIn, setOtpResendIn] = useState(0);

  // Countdown for re-send button
  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);
  const [refValid, setRefValid] = useState(null); // null | {valid, name}
  const fileInputRef = useRef(null);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Apply admin role preset once on mount (so the role-picker reflects the
  // requested ?role=volunteer or ?role=student from /admin/users → Create).
  useEffect(() => {
    if (isAdminCreate && adminRolePreset && form.role !== adminRolePreset) {
      setForm((f) => ({ ...f, role: adminRolePreset }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminCreate, adminRolePreset]);

  // Auto-verify referral code as user types (debounced)
  useEffect(() => {
    if (!form.referral_code || form.referral_code.length < 4) { setRefValid(null); return; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/public/verify-referral?code=${encodeURIComponent(form.referral_code)}`);
        setRefValid(data);
      } catch { setRefValid({ valid: false }); }
    }, 400);
    return () => clearTimeout(id);
  }, [form.referral_code]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file."); return; }
    if (file.size > 5_000_000) { toast.error("Image too large (max 5 MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF({ photo_url: data.url });
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const sendOtp = async () => {
    if (!form.phone || form.phone.length < 10) {
      toast.error("Enter a valid mobile number first."); return;
    }
    setOtpStage("sending");
    try {
      await api.post("/auth/otp/send", { mobile: form.phone, purpose: "registration" });
      toast.success(`OTP sent to ${form.phone}`);
      setOtpStage("sent");
      setOtpResendIn(45);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send OTP");
      setOtpStage("none");
    }
  };

  const verifyOtp = async () => {
    if ((otpCode || "").length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setOtpStage("verifying");
    try {
      await api.post("/auth/otp/verify", { mobile: form.phone, purpose: "registration", code: otpCode });
      setOtpStage("verified");
      toast.success("Mobile number verified ✅");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "OTP verification failed");
      setOtpStage("sent");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.role === "student" && !form.photo_url) {
      toast.error("Please upload your photo — required for exam proctoring verification.");
      return;
    }
    // OTP verification gate (public registration only — admin-create bypasses).
    if (!isAdminCreate && form.phone && otpStage !== "verified") {
      toast.error("Please verify your mobile number with OTP before submitting.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
      };

      if (isAdminCreate) {
        // Admin path: create user without auto-login.
        await api.post("/admin/users", payload);
        toast.success(`${form.name} created successfully — login email sent.`);
        navigate("/admin/students", { replace: true });
        return;
      }

      const result = await register(payload);
      if (result?.pending_approval) {
        setSubmitted(true);
        toast.success("Volunteer request submitted!");
        return;
      }
      toast.success(`Welcome, ${result.name}!`);
      navigate("/student", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = detail
        ? formatApiError(detail)
        : status
          ? `Registration failed (HTTP ${status})`
          : `Network error — please check your internet and try again. (${err?.message || "no response"})`;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted && form.role === "volunteer") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex justify-center mb-6"><Logo size={52} /></Link>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
              <Clock size={32} className="text-amber-600" />
            </div>
            <h1 className="heading text-2xl font-black mt-5">Request Submitted!</h1>
            <p className="text-sm text-slate-600 mt-3">
              Thank you for your interest in volunteering. Your request is <b>pending admin approval</b>.
            </p>
            <p className="text-xs text-slate-500 mt-3">
              We'll notify you at <b>{form.email}</b> once approved.
            </p>
            <Link to="/" className="mt-6 inline-block bg-brand hover:bg-[#172b66] text-white font-bold rounded-full px-6 py-2.5 text-sm" data-testid="vol-back-home">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12">
      <div className="w-full max-w-3xl mx-auto">
        <Link to="/" className="flex justify-center mb-6"><Logo size={52} /></Link>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-10">
          <div className="text-xs font-black uppercase tracking-[0.25em] text-[#F59E0B]">{isAdminCreate ? "Admin Console" : "PMR Welfare Foundation"}</div>
          <h1 className="heading text-2xl md:text-3xl font-black mt-1">{isAdminCreate ? `Create ${form.role === "volunteer" ? "Volunteer" : "Student"} Account` : "Registration Form"}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdminCreate
              ? "Fill the same form a user would. The new account will be created and a welcome email sent."
              : <>Fill in your details. Fields marked <span className="text-red-500">*</span> are required.</>}
          </p>

          {/* Role picker */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            {ROLES.map((r) => {
              const I = r.icon;
              const active = form.role === r.id;
              return (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setF({ role: r.id })}
                  className={`p-4 rounded-xl border-2 text-left transition ${active ? "border-brand bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`register-role-${r.id}-button`}
                >
                  <I size={20} className={active ? "text-brand" : "text-slate-500"} />
                  <div className="mt-2 font-bold">{r.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.desc}</div>
                </button>
              );
            })}
          </div>

          {form.role === "volunteer" && (
            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
              <Clock size={16} className="shrink-0 mt-0.5" />
              <div><b>Note:</b> Volunteer accounts require admin approval before login.</div>
            </div>
          )}

          {form.role === "student" && (
            <div className="mt-5">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-2">I'm here for</div>
              <div className="grid grid-cols-3 gap-2">
                {INTERESTS.map((it) => {
                  const I = it.icon;
                  const active = form.interest === it.id;
                  return (
                    <button
                      type="button"
                      key={it.id}
                      onClick={() => setF({ interest: it.id })}
                      className={`p-3 rounded-xl border-2 text-left transition ${active ? "border-[#F59E0B] bg-amber-50" : "border-slate-200"}`}
                      data-testid={`register-interest-${it.id}`}
                    >
                      <I size={16} className={active ? "text-[#F59E0B]" : "text-slate-500"} />
                      <div className="mt-1 font-bold text-xs">{it.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{it.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-8 space-y-8">
            {/* Photo upload */}
            {form.role === "student" && (
              <Section title="Profile Photo" subtitle="Used for proctor verification during exams. Must be a clear face photo.">
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <div className="w-28 h-28 rounded-2xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                      {form.photo_url ? (
                        <img src={ABS(form.photo_url)} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <Camera size={32} className="text-slate-300" />
                      )}
                    </div>
                    {form.photo_url && (
                      <button type="button" onClick={() => setF({ photo_url: "" })} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center" data-testid="register-photo-remove">
                        <XIcon size={12} />
                      </button>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => uploadPhoto(e.target.files?.[0])}
                      className="hidden"
                      data-testid="register-photo-file-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand hover:bg-[#172b66] disabled:opacity-60 text-white font-bold text-sm"
                      data-testid="register-upload-photo-button"
                    >
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {form.photo_url ? "Change Photo" : "Upload Photo"} <span className="text-red-300">*</span>
                    </button>
                    <div className="text-xs text-slate-500 mt-2">JPG / PNG / WEBP · Max 5 MB · Clear face, good lighting.</div>
                  </div>
                </div>
              </Section>
            )}

            {/* Account */}
            <Section title="Account Details">
              <Grid2>
                <Field label="Full Name" required v={form.name} on={(v) => setF({ name: v })} testid="register-name-input" />
                <Field label="Email" type="email" required v={form.email} on={(v) => setF({ email: v })} testid="register-email-input" />
                <Field label="Password" type="password" required minLength={6} v={form.password} on={(v) => setF({ password: v })} testid="register-password-input" />
                <Field label="Contact Number" required v={form.phone} on={(v) => { setF({ phone: v }); if (otpStage !== "none") { setOtpStage("none"); setOtpCode(""); } }} testid="register-phone-input" />
              </Grid2>

              {/* Mobile OTP verification — required for public registration */}
              {!isAdminCreate && form.phone && form.phone.length >= 10 && (
                <div className="mt-4 p-4 rounded-xl border border-amber-200 bg-amber-50/40">
                  <div className="flex items-start gap-2">
                    <div className="text-amber-600 mt-0.5">🔐</div>
                    <div className="flex-1">
                      <div className="text-sm font-bold text-slate-800">
                        Verify mobile number {otpStage === "verified" && <span className="text-emerald-600">✓ Verified</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        We'll send a 6-digit OTP to <b>{form.phone}</b>. This step is required to register.
                      </div>
                      {otpStage !== "verified" && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {otpStage === "sent" && (
                            <input
                              type="text" inputMode="numeric" maxLength={6}
                              value={otpCode}
                              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                              placeholder="6-digit OTP"
                              className="px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono tracking-widest text-center"
                              data-testid="register-otp-input"
                            />
                          )}
                          {otpStage === "sent" ? (
                            <button
                              type="button"
                              onClick={verifyOtp}
                              disabled={otpCode.length !== 6}
                              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg px-4 py-2"
                              data-testid="register-otp-verify-btn"
                            >Verify</button>
                          ) : (
                            <button
                              type="button"
                              onClick={sendOtp}
                              disabled={otpStage === "sending" || otpResendIn > 0}
                              className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-bold rounded-lg px-4 py-2"
                              data-testid="register-otp-send-btn"
                            >
                              {otpStage === "sending" ? "Sending..." : otpResendIn > 0 ? `Resend in ${otpResendIn}s` : "Send OTP"}
                            </button>
                          )}
                          {otpStage === "sent" && otpResendIn === 0 && (
                            <button type="button" onClick={sendOtp} className="text-xs text-brand font-semibold hover:underline">Resend</button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Section>

            {form.role === "student" ? (
              <>
                {/* Personal */}
                <Section title="Personal Details">
                  <Grid2>
                    <Field label="Date of Birth" type="date" v={form.date_of_birth} on={(v) => setF({ date_of_birth: v })} testid="register-dob-input" />
                    <Field label="Age" type="number" v={form.age} on={(v) => setF({ age: v })} testid="register-age-input" />
                    <Select label="Gender" v={form.gender} options={GENDERS} on={(v) => setF({ gender: v })} testid="register-gender-select" />
                    <Field label="Occupation" v={form.occupation} on={(v) => setF({ occupation: v })} placeholder="e.g. Student" testid="register-occupation-input" />
                  </Grid2>
                  <Field label="Address" v={form.address} on={(v) => setF({ address: v })} testid="register-address-input" textarea />
                  <Grid2>
                    <Field label="State" v={form.state} on={(v) => setF({ state: v })} testid="register-state-input" />
                    <Field label="City" v={form.city} on={(v) => setF({ city: v })} testid="register-city-input" />
                  </Grid2>
                </Section>

                {/* Academic */}
                <Section title="Academic Details">
                  <Grid2>
                    <Field label="University" v={form.university} on={(v) => setF({ university: v })} testid="register-university-input" />
                    <Field label="College Name" v={form.college} on={(v) => setF({ college: v })} testid="register-college-input" />
                    <Field label="Department" v={form.department} on={(v) => setF({ department: v })} testid="register-department-input" />
                    <Field label="Honors" v={form.honors} on={(v) => setF({ honors: v })} testid="register-honors-input" />
                  </Grid2>
                  <Field label="Stream / Specialisation" v={form.stream} on={(v) => setF({ stream: v })} placeholder="e.g. Economics Honours, B.Tech CSE" testid="register-stream-input" />
                  <Grid2>
                    <Field label="University Roll Number" v={form.university_roll_number} on={(v) => setF({ university_roll_number: v })} placeholder="e.g. 2024CSE-101" testid="register-univ-roll-input" />
                    <Field label="Registration Number" v={form.registration_number} on={(v) => setF({ registration_number: v })} placeholder="University registration / enrolment no." testid="register-reg-no-input" />
                  </Grid2>
                </Section>

                {/* Family */}
                <Section title="Family Details">
                  <Grid2>
                    <Field label="Father's Name" v={form.father_name} on={(v) => setF({ father_name: v })} testid="register-father-name-input" />
                    <Field label="Father's Occupation" v={form.father_occupation} on={(v) => setF({ father_occupation: v })} testid="register-father-occ-input" />
                    <Field label="Mother's Name" v={form.mother_name} on={(v) => setF({ mother_name: v })} testid="register-mother-name-input" />
                    <Field label="Mother's Occupation" v={form.mother_occupation} on={(v) => setF({ mother_occupation: v })} testid="register-mother-occ-input" />
                  </Grid2>
                </Section>

                {/* Emergency */}
                <Section title="Emergency Contact">
                  <Grid2>
                    <Field label="Contact Person" v={form.emergency_contact_name} on={(v) => setF({ emergency_contact_name: v })} testid="register-em-name-input" />
                    <Field label="Relation" v={form.emergency_contact_relation} on={(v) => setF({ emergency_contact_relation: v })} placeholder="e.g. Sister, Father" testid="register-em-rel-input" />
                  </Grid2>
                  <Field label="Contact Number" v={form.emergency_contact_phone} on={(v) => setF({ emergency_contact_phone: v })} testid="register-em-phone-input" />
                </Section>
              </>
            ) : (
              <Section title="About You">
                <Field
                  label="Why do you want to volunteer?" textarea
                  v={form.stream}
                  on={(v) => setF({ stream: v })}
                  placeholder="Share your motivation, skills and availability..."
                  testid="register-motivation-input"
                />
              </Section>
            )}

            {/* Referral code (optional, for both roles) */}
            <Section title="Referral (Optional)" subtitle="Got a referral code from a PMR Welfare volunteer? Enter it here.">
              <div>
                <label className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Referral Code</label>
                <input
                  type="text"
                  value={form.referral_code}
                  onChange={(e) => setF({ referral_code: e.target.value.toUpperCase() })}
                  placeholder="e.g. PMR2X9"
                  maxLength={8}
                  className={`mt-1 w-full px-4 py-2.5 rounded-lg border outline-none text-sm font-mono uppercase ${refValid?.valid === false ? "border-red-300 bg-red-50" : refValid?.valid ? "border-emerald-400 bg-emerald-50" : "border-slate-300 focus:border-brand"}`}
                  data-testid="register-referral-code"
                />
                {refValid?.valid && (
                  <div className="mt-2 text-xs font-bold text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Referred by <b>{refValid.referrer_name}</b>
                  </div>
                )}
                {form.referral_code && refValid?.valid === false && (
                  <div className="mt-2 text-xs font-bold text-red-600">Invalid referral code.</div>
                )}
              </div>
            </Section>

            <button
              type="submit"
              disabled={loading || uploading}
              className="w-full bg-brand hover:bg-[#172b66] disabled:opacity-60 text-white font-black text-sm tracking-wide rounded-xl py-4 flex items-center justify-center gap-2"
              data-testid="register-submit-button"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {form.role === "volunteer" ? "Submit Volunteer Request" : "Create Student Account"}
            </button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-brand font-bold" data-testid="register-to-login-link">Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="pb-2 mb-4 border-b border-slate-200">
        <h2 className="heading text-sm font-black uppercase tracking-[0.2em] text-brand">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, v, on, type = "text", required, minLength, testid, placeholder, textarea }) {
  return (
    <div>
      <label className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">{label} {required && <span className="text-red-500">*</span>}</label>
      {textarea ? (
        <textarea
          rows={2}
          value={v}
          onChange={(e) => on(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-brand outline-none resize-none text-sm"
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          required={required}
          minLength={minLength}
          value={v}
          onChange={(e) => on(e.target.value)}
          placeholder={placeholder}
          className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-brand outline-none text-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
}

function Select({ label, v, options, on, testid }) {
  return (
    <div>
      <label className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <select
        value={v}
        onChange={(e) => on(e.target.value)}
        className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-brand outline-none text-sm bg-white"
        data-testid={testid}
      >
        <option value="">Select...</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
